// BlueprintLotteryScene.js - 图纸和抽奖 NPC 场景
import InputHelper from './modules/InputHelper.js';
import BlueprintSystem from './modules/BlueprintSystem.js';
import LotterySystem from './modules/LotterySystem.js';
import GameStateStore from './modules/GameStateStore.js';
import BlueprintLotteryUI from './modules/Lottery/BlueprintLotteryUI.js';
import BlueprintLotteryDisplay from './modules/Lottery/BlueprintLotteryDisplay.js';

export default class BlueprintLotteryScene extends Phaser.Scene {
    constructor() {
        super('BlueprintLotteryScene');
    }

    init(data) {
        this.npcName = data.npcName || '商人';
        this.playerData = GameStateStore.getPlayer();
        this.selectedItemIndex = 0;
        this.menuOptions = ['购买图纸', '抽奖', '离开'];
        this.currentMode = 'menu';
        this.previousSceneKey = data?.previousSceneKey || 'WorldScene';
        this.blueprintData = null;
    }

    create() {
        this.ui = new BlueprintLotteryUI(this);
        this.display = new BlueprintLotteryDisplay(this, this.ui);
        this.ui.create(this.npcName);

        const equipmentData = this.cache.json.get('vehicle_equipment');
        if (equipmentData) {
            this.blueprintData = BlueprintSystem.getAllBlueprints(equipmentData);
        }

        this.setupControls();
        this.refreshHud();
        this.updateDisplay();

        this.events.on('resume', () => {
            this.playerData = GameStateStore.getPlayer();
            this.refreshHud();
            this.updateDisplay();
        });
    }

    shutdown() {
        this.destroyMenuInput();
    }

    setupControls() {
        this.menuInput = InputHelper.bindMenuKeyboard(this, {
            onUp: () => this.moveSelection(-1),
            onDown: () => this.moveSelection(1),
            onSelect: () => this.selectItem(),
            onCancel: () => {
                if (this.currentMode === 'menu') {
                    this.exitScene();
                } else {
                    this.currentMode = 'menu';
                    this.selectedItemIndex = 0;
                    this.updateDisplay();
                }
            }
        });
    }

    destroyMenuInput() {
        if (this.menuInput) {
            this.menuInput.destroy();
            this.menuInput = null;
        }
    }

    refreshHud() {
        this.ui.updateMoney(this.playerData.money);
        this.ui.updateTicketCount(LotterySystem.getTicketCount(this.playerData));
        this.ui.updateModeText(this.currentMode);
    }

    onListInteract(index, action) {
        this.selectedItemIndex = index;
        this.updateDisplay();
        if (action === 'click') {
            this.selectItem();
        }
    }

    moveSelection(direction) {
        if (this.currentMode === 'menu') {
            this.selectedItemIndex = Phaser.Math.Wrap(
                this.selectedItemIndex + direction,
                0,
                this.menuOptions.length
            );
        } else if (this.currentMode === 'blueprint' && this.blueprintData?.length) {
            this.selectedItemIndex = Phaser.Math.Wrap(
                this.selectedItemIndex + direction,
                0,
                this.blueprintData.length + 1
            );
        }
        this.updateDisplay();
    }

    selectItem() {
        if (this.currentMode === 'menu') {
            const option = this.menuOptions[this.selectedItemIndex];
            if (option === '购买图纸') {
                if (!this.blueprintData?.length) {
                    this.ui.showMessage('暂时没有图纸出售...', '#ff6666');
                    return;
                }
                this.currentMode = 'blueprint';
                this.selectedItemIndex = 0;
            } else if (option === '抽奖') {
                this.openLottery();
                return;
            } else {
                this.exitScene();
                return;
            }
        } else if (this.currentMode === 'blueprint') {
            if (!this.blueprintData?.length) return;
            if (this.selectedItemIndex === this.blueprintData.length) {
                this.currentMode = 'menu';
                this.selectedItemIndex = 0;
            } else {
                this.purchaseBlueprint(this.blueprintData[this.selectedItemIndex]);
            }
        }

        this.refreshHud();
        this.updateDisplay();
    }

    purchaseBlueprint(blueprint) {
        if (BlueprintSystem.hasBlueprint(this.playerData, blueprint.equipmentId)) {
            this.ui.showMessage(`你已经拥有 ${blueprint.equipmentName} 图纸了`, '#ff6666');
            return;
        }
        if (this.playerData.money < blueprint.price) {
            this.ui.showMessage('金钱不足！', '#ff6666');
            return;
        }

        this.playerData.money -= blueprint.price;
        this.playerData.inventory.push(
            BlueprintSystem.createBlueprint(blueprint.equipmentId, blueprint.equipmentName)
        );
        GameStateStore.patchPlayer({ money: this.playerData.money, inventory: this.playerData.inventory });

        this.refreshHud();
        this.ui.showMessage(`购买了 ${blueprint.name}！`, '#66ff99');
        this.updateDisplay();
    }

    openLottery() {
        const ticketCount = LotterySystem.getTicketCount(this.playerData);
        if (ticketCount === 0) {
            this.ui.showMessage('需要抽奖券！完成副本可获得。', '#ffcc66');
            return;
        }

        const currentSceneKey = this.scene.key;
        this.scene.pause(currentSceneKey);
        this.scene.launch('LotteryScene', {
            previousSceneKey: currentSceneKey,
            ticketType: 'lottery_ticket_tier1'
        });
    }

    updateDisplay() {
        this.refreshHud();
        const onInteract = (index, action) => this.onListInteract(index, action);

        if (this.currentMode === 'menu') {
            this.display.showMenu(
                this.menuOptions,
                this.selectedItemIndex,
                LotterySystem.getTicketCount(this.playerData),
                onInteract
            );
        } else if (this.currentMode === 'blueprint') {
            this.display.showBlueprintList(
                this.blueprintData || [],
                this.selectedItemIndex,
                this.playerData,
                onInteract
            );
        }
    }

    exitScene() {
        this.destroyMenuInput();
        const previousScene = this.scene.get(this.previousSceneKey);
        if (previousScene) {
            this.scene.resume(this.previousSceneKey);
        }
        this.scene.stop();
    }
}
